import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import './ProductCard.css';

function ProductCard({ product, isAdmin = false, large = false, showQuickAdd = false }) {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // Check if item is in cart
  useEffect(() => {
    const checkCart = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API}/api/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const isInCart = data.items?.some(item => item.product?._id === product._id);
          setInCart(isInCart);
        }
      } catch (error) {
        console.error('Error checking cart:', error);
      }
    };
    checkCart();
  }, [product._id]);
  
  // Calculate sale price if discount exists
  const hasDiscount = product.discount && product.discount > 0;
  let salePrice = product.price;
  
  if (hasDiscount) {
    if (product.discountType === 'percentage') {
      salePrice = product.price * (1 - product.discount / 100);
    } else if (product.discountType === 'fixed') {
      salePrice = product.price - product.discount;
    }
    salePrice = Math.max(0, salePrice); // Ensure price doesn't go negative
  }

  const handleQuickAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const token = localStorage.getItem('token');
    if (!token) {
      setShowLoginModal(true);
      return;
    }

    setAddingToCart(true);

    try {
      if (inCart) {
        // Remove from cart
        const response = await fetch(`${process.env.REACT_APP_API}/api/cart/${product._id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to remove from cart');
        }

        setInCart(false);
        // Dispatch cart update event with details
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { action: 'removed', productName: product.name }
        }));
      } else {
        // Add to cart
        const response = await fetch(`${process.env.REACT_APP_API}/api/cart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ productId: product._id, quantity: 1 })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add to cart');
        }

        setInCart(true);
        // Dispatch cart update event with details
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { action: 'added', productName: product.name }
        }));
      }
    } catch (error) {
      console.error('Cart operation error:', error);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleCardClick = () => {
    if (!isAdmin) {
      navigate(`/product/${product._id}`);
    }
  };

  const isMobile = window.innerWidth <= 600;
  const cardContent = (
    <motion.div 
      className={`product-card ${large ? 'product-card-large' : ''}`}
      onClick={handleCardClick}
      whileHover={{ y: -8, transition: { duration: 0.2 } }}
      whileTap={{ scale: 0.98 }}
      style={{
        background: '#fff',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: 'none',
        transition: 'box-shadow 0.3s ease',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 320 : '100%',
        border: `2px solid ${isMobile ? '#7a9c3a' : '#2a4d1b'}`,
        width: '100%',
        maxWidth: 340,
        minWidth: 0
      }}
    >
      {/* Image Section */}
      <div style={{
        position: 'relative',
        background: '#fff',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: isMobile ? 110 : 200,
        overflow: 'visible',
        borderRadius: 14
      }}>
        {hasDiscount ? (
          <span style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: '#7a9c3a',
            color: '#fff',
            padding: '5px 14px',
            borderRadius: 14,
            fontSize: isMobile ? '0.7rem' : '0.8rem',
            fontWeight: 700,
            letterSpacing: '0.5px',
            zIndex: 10,
            lineHeight: 1,
            textTransform: 'uppercase',
            boxShadow: 'none'
          }}>
            SALE
          </span>
        ) : null}
        <img
          src={product.image && product.image.trim() !== '' ? product.image : '/default-plant.png'}
          alt={product.name}
          className="product-image"
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          style={{ 
            width: isMobile ? '80%' : '85%', 
            height: isMobile ? '80%' : '85%', 
            objectFit: 'contain',
            opacity: imageLoaded ? 1 : 0, 
            transition: 'opacity 0.3s ease',
            borderRadius: 16,
            border: `1px solid ${isMobile ? '#2a4d1b' : '#e0e0e0'}`,
            background: '#fff',
            boxSizing: 'border-box',
            boxShadow: 'none'
          }}
        />
      </div>

      {/* Content Section */}
      <div style={{ 
        flex: 1,
        padding: isMobile ? '10px 8px 0 8px' : '16px 12px 0 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        background: 'none',
        justifyContent: 'flex-start',
        minHeight: 0
      }}>
        {/* Product Name */}
        <div style={{ 
          fontWeight: 700, 
          fontSize: isMobile ? '0.95rem' : '1.1rem', 
          marginBottom: 8,
          color: '#2a2a2a',
          letterSpacing: '0.3px',
          width: '100%',
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          display: 'block',
          lineHeight: 1.2
        }}>
          {product.name}
        </div>
        {/* Ratings */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          marginBottom: 10,
          height: 22
        }}>
          {[1,2,3,4,5].map((rating) => {
            const avgRating = product.averageRating || 0;
            const isActive = rating <= Math.round(avgRating);
            return (
              <svg key={rating} width={isMobile ? 15 : 18} height={isMobile ? 15 : 18} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
                <path
                  d="M50,90 L20,50 Q10,30 20,20 Q30,10 40,20 L50,30 L60,20 Q70,10 80,20 Q90,30 80,50 Z"
                  fill={isActive ? '#7a9c3a' : '#e2e8f0'}
                />
                <path
                  d="M50,30 Q25,25 20,50 L50,90 Z"
                  fill={isActive ? '#5a7a2a' : '#d1d5db'}
                  opacity="0.9"
                />
                <path
                  d="M50,30 Q75,25 80,50 L50,90 Z"
                  fill={isActive ? '#b6e67a' : '#e5e7eb'}
                  opacity="0.9"
                />
                <path
                  d="M50,30 L50,15 Q52,8 50,5 Q48,8 50,15 L50,90 Z"
                  fill={isActive ? '#c3e84a' : '#e8e8e8'}
                  opacity="0.95"
                />
              </svg>
            );
          })}
        </div>
        {/* Price */}
        <div style={{ 
          fontSize: isMobile ? '1rem' : '1.1rem', 
          fontWeight: 700,
          color: '#7a9c3a',
          marginBottom: isMobile ? 10 : 14,
          letterSpacing: '0.3px'
        }}>
          {hasDiscount ? (
            <>
              <span style={{ textDecoration: 'line-through', color: '#bbb', fontSize: isMobile ? '0.85rem' : '0.95rem', marginRight: 8 }}>
                ₱{Number(product.price).toFixed(2)}
              </span>
              ₱{salePrice.toFixed(2)}
            </>
          ) : (
            <>₱{Number(product.price).toFixed(2)}</>
          )}
        </div>
      </div>

      {/* Add to Cart Button */}
      {showQuickAdd && !isAdmin && (
        <motion.button
          onClick={handleQuickAddToCart}
          disabled={addingToCart || product.stock === 0}
          whileHover={{ scale: product.stock === 0 ? 1 : 1.03, backgroundColor: product.stock === 0 ? '#e0e0e0' : '#2a4d1b' }}
          whileTap={{ scale: product.stock === 0 ? 1 : 0.98 }}
          style={{
            width: '100%',
            padding: isMobile ? '10px' : '12px',
            borderRadius: 10,
            background: product.stock === 0 ? '#e0e0e0' : '#2a4d1b',
            border: 'none',
            color: product.stock === 0 ? '#999' : '#fff',
            fontSize: isMobile ? '0.85rem' : '0.95rem',
            fontWeight: 700,
            cursor: product.stock === 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: addingToCart ? 0.7 : 1,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            marginTop: 'auto'
          }}
          title={product.stock === 0 ? 'Out of stock' : inCart ? 'Remove from cart' : 'Add to cart'}
        >
          {addingToCart ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              style={{ fontSize: isMobile ? '0.85rem' : '0.95rem' }}
            >
              ⏳
            </motion.div>
          ) : product.stock === 0 ? 'Out of Stock' : inCart ? 'Remove' : 'Add to Cart'}
        </motion.button>
      )}
      <ConfirmModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onConfirm={() => navigate('/login')}
        title="Login Required"
        message="Please login to add items to your cart"
        confirmText="Go to Login"
        cancelText="Cancel"
        type="info"
      />
    </motion.div>
  );

  return cardContent;
}

export default ProductCard;
